<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h1 class="text-uppercase">
          <span>{{ isEditPage ? $t('edit') : $t('create') }} {{ $t('spotify_campaign') }}</span>
        </h1>
      </div>
      <div class="d-flex flex-row justify-content-center align-items-center gap-3 me-2">
        <div class="d-flex flex-row justify-content-center align-items-center gap-2" v-if="isSavingDraft">
          <Loader
            :size="'small'"
            :align="'start'"
          />
          {{ $t('saving_draft') }}
        </div>
        <Button
          class="btn btn-color10"
          v-b-modal.cancel-campaign-upsert-modal
        >
        {{ $t('cancel') }}
        </Button>
        <CancelCampaignUpsertModal
          :campaign="campaign"
          step="campaign"
        />
      </div>
    </div>
    <!-- CAMPAIGN FORM -->
    <div class="mt-3" v-if="!isEditPage || (isEditPage && campaign)">
      <div class="campaign-wrapper position-relative">
        <!-- INIT STEP -->
        <template v-if="step === 'init'">
          <div class="col-lg-8 mx-auto">
            <div class="card border-dashed border-color10">
              <div class="card-body">
                <p>
                  {{ $t('easy_campaign_pixel_roads') }}
                </p>
                <p>
                  {{ $t('important_thoughts_for_campaign') }}
                </p>
                <ul>
                  <li>{{ $t('campaign_duration') }}</li>
                  <li>{{ $t('available_budget') }}*<br/>
                    <span class="font-size-xSmall">* {{ $t('daily_budget_calculation') }}</span>
                  </li>
                  <li> {{ $t('max_audio_30_seconds') }} </li>
                  <li> {{ $t('describe_audience_details') }} </li>
                </ul>
                <div class="mt-4 text-center">
                  <Button
                    class="btn btn-color10"
                    @click="changeStep('campaign')"
                  >
                    {{ $t('start2') }}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </template> <!-- END INIT STEP -->
        <!-- HEADING -->
        <section class="my-4" v-if="step !== 'init'">
          <div class="text-center">
            <p>
              {{ stepDescription }}
            </p>
            <div class="mb-3 text-start">
              <a href="#" class="link-color2" @click.prevent="changeStep(previousStep)" v-if="previousStep"><i class="fa fa-angle-left me-2"></i>{{ $t('back') }}</a>
            </div>
          </div>
        </section> <!-- END HEADING -->
        <!-- CAMPAIGN STEP -->
        <template v-if="step === 'campaign'">
          <section class="">
            <div class="accordion accordion-flush" id="accordionFlushExample">
              <!-- FORM CAMPAIGN ACCORDION ITEM -->
              <div class="accordion-item border-bottom border-dashed">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" block v-b-toggle.accordion-1 variant="info">
                    {{ $t('campaign') }}
                  </button>
                </h2>
                <b-collapse id="accordion-1" visible accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <form @submit.prevent="" class="default-form">
                      <div class="d-flex flex-column flex-md-row">
                        <div class="col-12 col-md-6 pe-md-10">
                          <!-- GENERAL DATA HEADER -->
                          <div class="mb-2">
                            <div class="d-flex align-items-center justify-content-between">
                              <h3 class="text-uppercase font-size-regular fw-500">
                                <span>{{ $t('general_campaign_data') }}</span>
                              </h3>
                            </div>
                          </div> <!-- END GENERAL DATA HEADER -->
                          <!-- ADVERTISER -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">
                              {{ $t('advertiser') }}*
                            </label>
                            <div v-if="advertisersLoader">
                              <Loader
                                :size="'small'"
                                :align="'start'"
                              />
                            </div>
                            <template v-else>
                              <div class="custom-select">
                                <select
                                  class="form-control"
                                  v-model.trim="form.advertiser_id"
                                  :disabled="campaign !== null && campaign.status !== 'DRAFT'"
                                >
                                  <option disabled value="">{{ $t('select') }}</option>
                                  <template v-if="advertisers.length > 0">
                                    <option v-for="(lAdvertiser, index) in advertisers" :key="index" :value="lAdvertiser.id">{{ lAdvertiser.name }}</option>
                                  </template>
                                </select>
                              </div>
                              <p
                                class="font-size-xSmall fw-bold mt-2 error-message-card"
                                v-if="selectedAdvertiser && selectedAdvertiser.status === 'ERROR'"
                              >
                                {{ $t('advertiser_creation_issue_access_page_try_again', {name:selectedAdvertiser.name}) }}
                              </p>
                            </template>
                          </div> <!-- END ADVERTISER -->
                          <!-- NAME -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('campaign_name') }}*</label>
                            <input type="text" class="form-control" :placeholder="$t('campaign_name')" v-model.trim="form.name" />
                          </div> <!-- END NAME -->
                          <!-- DATES -->
                          <div class="form-group mb-7">
                            <label class="form-label fw-bold">{{ $t('duration') }}*</label>
                            <div class="d-flex">
                              <!-- START DATE -->
                              <div class="col-lg-4">
                                <DateTimeInput
                                  v-model.trim="form.start_date"
                                  format="dd/MM/yyyy"
                                  type="date"
                                  theme="basic"
                                  class="mx-2"
                                  :placeholder="$t('start')"
                                  :readonly="campaign && campaign.has_started"
                                />
                              </div> <!-- END START DATE -->
                              <!-- END DATE -->
                              <div class="col-lg-4">
                                <DateTimeInput
                                  v-model.trim="form.end_date"
                                  format="dd/MM/yyyy"
                                  type="date"
                                  theme="basic"
                                  class="mx-2"
                                  :placeholder="$t('end')"
                                />
                              </div> <!-- END END DATE -->
                            </div>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.start_date && !isCampaignStartDateValid"
                            >
                              {{ $t('start_date_min_3_days_ahead') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.end_date && !isCampaignEndDateValid"
                            >
                              {{ $t('end_date_validation') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.start_date && form.end_date && isCampaignEndDateBeforeStartDate"
                            >
                              {{ $t('end_date_not_before_start_date') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 alert-message-card"
                              v-if="form.start_date && form.end_date && isCampaignPeriodBelowRecommended"
                            >
                              {{ $t('campaign_minimum_duration_10_days') }}
                            </p>
                          </div> <!-- END DATES -->
                          <!-- GENERAL DATA HEADER -->
                          <div class="my-2">
                            <div class="d-flex align-items-center justify-content-between">
                              <h3 class="text-uppercase font-size-regular fw-500">
                                <span>{{ $t('budget') }}</span>
                              </h3>
                            </div>
                          </div> <!-- END GENERAL DATA HEADER -->
                          <!-- AMOUNT -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('campaign_budget') }}*</label>
                            <Money
                              class="form-control mb-3"
                              v-model.trim="form.amount"
                              v-bind="money"
                            />
                            <template v-if="selectedAdvertiser">
                              <div class="d-flex flex-column flex-wrap">
                                <div class="">
                                  <p
                                    class="font-size-xSmall fw-bold mt-2 error-message-card"
                                    v-if="hasCampaignExceededAvailableAdvertiserAmount"
                                  >
                                    {{ $t('campaign_budget_cannot_exceed') }}
                                  </p>
                                  <p class="font-size-xSmall fw-bold">
                                    {{ $t('available_balance') }}: {{ selectedAdvertiser.available_amount / 100 | formatMoney(selectedAdvertiser.currency) }}
                                  </p>
                                </div>
                                <div class="text-lg-end d-flex align-items-center justify-content-center justify-content-lg-end">
                                  <Button
                                    class="btn btn-color10 btn-xs me-2"
                                    @click="addAllAvailableAmountToCampaign"
                                  >
                                    {{ $t('use_all_balance') }}
                                  </Button>
                                </div>
                              </div>
                            </template>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="!checkIfDailyBudgetIsBiggerThanMinimumDailyBudget"
                            >
                              {{ $t('recommended_minimum_daily_budget', {value:minimumDailyBudget, date:this.$options.filters.formatDate(suggestedEndDate)}) }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.amount > 0 && form.amount < 5000"
                            >
                              {{ $t('minimum_audio_campaign_amount') }} {{ 5000 | formatMoney(selectedAdvertiser ? selectedAdvertiser.currency : null) }}.
                            </p>
                          </div><!-- END AMOUNT -->
                          <!-- DESCRIPTION TARGET -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">Descrição de Audiência*</label>
                            <input type="text" class="form-control" :placeholder="'Descrição de Audiência'" v-model.trim="form.audience_description" />
                            <p class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2">
                              {{ $t('describe_audience_details') }}
                            </p>
                          </div><!-- END DESCRIPTION TARGET -->
                        </div>
                        <div class="col-12 col-md-6 ps-md-5">
                          <!-- GENERAL DATA HEADER -->
                          <div class="mb-2 mt-4">
                            <div class="d-flex align-items-center justify-content-between">
                              <h3 class="text-uppercase font-size-regular fw-500">
                                <span>{{ $t('segmentation') }}</span>
                              </h3>
                            </div>
                          </div> <!-- END GENERAL DATA HEADER -->
                          <div class="">
                            <!-- COUNTRIES -->
                            <div class="form-group mb-3 me-2">
                              <div class="d-flex align-items-center mb-2">
                                <label class="form-label fw-bold mb-0">
                                  {{ $t('countries') }}
                                  <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                                </label>
                              </div>
                              <template v-if="countriesLoader">
                                <Loader
                                  :size="'small'"
                                  :align="'start'"
                                />
                              </template>
                              <template v-else>
                                <Multiselect
                                  v-model="selectedCountries"
                                  :placeholder="$t('search2')"
                                  label="label"
                                  track-by="code"
                                  :options="countryOptions"
                                  :multiple="true"
                                />
                              </template>
                            </div> <!-- END COUNTRIES -->
                            <!-- STATES -->
                            <div class="form-group mb-3 me-2" v-if="selectedCountries.length > 0">
                              <div class="d-flex align-items-center mb-2">
                                <label class="form-label fw-bold mb-0">
                                  {{ $t('states') }}
                                  <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                                </label>
                                <Button
                                  class="btn-xs btn-color10 py-1 ms-2"
                                  @click="selectAllStates"
                                  v-if="stateOptions.length > 0"
                                >
                                  {{ $t('select_all') }}
                                </Button>
                                <Button
                                  class="btn-xs btn-color10 ms-2 py-1"
                                  @click="clearSelection('states')"
                                  v-if="form.states.length > 0"
                                >
                                  {{ $t('clear') }}
                                </Button>
                              </div>
                              <template v-if="statesLoader">
                                <Loader
                                  :size="'small'"
                                  :align="'start'"
                                />
                              </template>
                              <template v-else>
                                <Multiselect
                                  v-model="form.states"
                                  :placeholder="$t('search2')"
                                  label="label"
                                  track-by="code"
                                  :options="stateOptions"
                                  :multiple="true"
                                  :disabled="selectedCountries.length == 0"
                                />
                                <div
                                  class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2"
                                  v-if="selectedCountries.length == 0"
                                >
                                  <p
                                    class="mb-0"
                                  >
                                    {{ $t('select_at_least_one_country_for_state_selection') }}
                                  </p>
                                </div>
                              </template>
                            </div> <!-- END STATES -->
                            <!-- CITIES -->
                            <div class="form-group mb-3" v-if="selectedCountries.length > 0">
                              <label class="form-label fw-bold">
                                {{ $t('cities') }}
                                <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                              </label>
                              <template v-if="citiesLoader">
                                <Loader
                                  :size="'small'"
                                  :align="'start'"
                                />
                              </template>
                              <template v-else>
                                <Multiselect
                                  v-model="form.cities"
                                  :placeholder="$t('search2')"
                                  label="label"
                                  track-by="code"
                                  :options="citiesOptions"
                                  :multiple="true"
                                  :disabled="selectedCountries.length == 0"
                                />
                                <div
                                  class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2"
                                  v-if="selectedCountries.length == 0"
                                >
                                  <p
                                    class="mb-0"
                                  >
                                    {{ $t('select_at_least_one_country_for_city_selection') }}
                                  </p>
                                </div>
                              </template>
                            </div> <!-- END CITIES -->
                            <div v-if="showAlertOnCitiesAndStatesSelection && (!countriesLoader && !citiesLoader && !statesLoader)">
                              <p class="fw-bold alert-message-card text-color2 font-size-xRegular mt-2" v-html="showAlertOnCitiesAndStatesSelection">
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- ACTION -->
                      <div class="mt-10 text-center">
                        <Button
                          class="btn btn-color10"
                          @click="changeAccordionItem('accordion-2')"
                          :disabled="!isCampaignStepComplete || !checkIfDailyBudgetIsBiggerThanMinimumDailyBudget"
                        >
                        {{ $t('next') }}
                        </Button>
                      </div> <!-- END ACTION -->
                    </form>
                  </div>
                </b-collapse>
              </div><!-- END FORM CAMPAIGN ACCORDION ITEM -->

              <!-- CREATIVE ACCORDION ITEM -->
              <div class="accordion-item border-bottom border-dashed">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" type="button" block v-b-toggle.accordion-2 variant="info" :disabled="!isCampaignStepComplete || !checkIfDailyBudgetIsBiggerThanMinimumDailyBudget">
                    {{ $t('creatives') }}
                  </button>
                </h2>
                <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <div class="text-center">
                      <h2 class="highlighted-color1">
                        <span>{{ $t('select_creative') }}</span>
                      </h2>
                      <p>{{ $t('select_creative_or_create') }}</p>
                    </div>
                    <DestroyCreativeModal
                      :creative="selectedCreative"
                      @creative-destroyed="destroyCreative"
                    />
                    <div class="mb-5">
                      <SelectCreativeModal
                        :advertiserId="form.advertiser_id"
                        format="AUDIO"
                        @creative-selected="handleSelectedCreative"
                      />
                    </div>
                    <div
                      class="d-flex creative-item flex-wrap align-items-center bg-white mt-3 p-3"
                      v-for="(creative, index) in form.creatives"
                      :key="index"
                    >
                      <div class="col-lg-3">
                        <img :src="creative.file" alt="" style="max-width: 100px;" />
                      </div>
                      <div class="col-lg-8 text-start">
                        <p class="mb-0">{{ creative.name }}</p>
                        <p class="mb-0">{{ creative.format | formatCreativeFormat }}  | {{ creative.duration }} {{ $t('seconds') }}</p>
                      </div>
                      <div class="col-lg-1">
                        <Button
                          class="btn-xs btn-circle btn-danger font-size-small p-0"
                          @click="openDestroyCreativeModal(creative)"
                          v-b-tooltip.hover :title="$t('remove_creative')"
                        >
                          <i class="fa fa-trash"></i>
                        </Button>
                      </div>
                    </div>
                  </div>
                </b-collapse>
              </div><!-- END CREATIVE ACCORDION ITEM -->
            </div>
            <!-- ACTION -->
            <div class="text-center mt-5 ">
              <!-- MESSAGES -->
              <div
                class="mb-2 alert-message-card text-color2 font-size-small px-3 py-2"
                v-if="loader"
              >
                <p
                  class="mb-0"
                >
                  {{ $t('this_action_may_take_a_few_minutes') }}
                </p>
              </div> <!-- END MESSAGES -->
              <Button
                class="btn btn-color10"
                @click="clickSubmit"
                :disabled="!isFormComplete || isSavingDraft"
                :loading="loader"
              >
                {{ campaign ? $t('save_campaign') : $t('create_campaign') }}
              </Button>
            </div> <!-- END ACTION -->
          </section>
        </template> <!-- END CAMPAIGN STEP -->
      </div>
    </div> <!-- END CAMPAIGN FORM -->
  </div>
</template>

<script>

import AdvertisersService from '@/modules/advertisers/services/advertisers-service'
import CampaignsService from '@/modules/campaigns/services/campaigns-service'
import { getMoneyFormat } from '@/utils'
import HelpersService from '@/modules/helpers/services/helpers-service'
import NotificationService from '@/modules/notifications/services/notifications-service'
import variables from '@/mixins/variables'
import DestroyCreativeModal from '@/components/campaigns/UpsertForm/DestroyCreativeModal'

import Button from '@/components/common/Button/Button'
import CancelCampaignUpsertModal from '@/components/campaigns/Modals/CancelCampaignUpsertModal'
import DateTimeInput from '@/components/common/Input/DateTimeInput'
import Loader from '@/components/common/Loader/Loader'
import { Money } from 'v-money'
import Multiselect from 'vue-multiselect'
import SelectCreativeModal from '@/components/creatives/SelectCreative'

import { mapState } from 'vuex'
import moment from 'moment'

export default {
  mixins: [variables],
  props: ['baseCampaign'],
  components: {
    Button,
    CancelCampaignUpsertModal,
    DateTimeInput,
    Loader,
    Money,
    Multiselect,
    SelectCreativeModal,
    DestroyCreativeModal
  },
  data () {
    return {
      autoSaveInterval: null,
      advertisers: [],
      advertisersLoader: false,
      campaign: null,
      cities: [],
      citiesLoader: false,
      countries: [],
      countriesLoader: false,
      currentCreativeSelection: '',
      form: {
        advertiser_id: '',
        audience_description: '',
        name: '',
        start_date: '',
        end_date: '',
        amount: '',
        type: 'AUDIO',
        states: [],
        cities: [],
        platform: 'GOOGLE',
        creatives: []
      },
      creatives: [],
      isSavingDraft: false,
      loader: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },
      selectedCountries: [],
      selectedCreative: '',
      states: [],
      statesLoader: false,
      step: 'init',
      minimumDailyBudget: 100
    }
  },
  mounted () {
    if (this.isEditPage) {
      this.step = 'campaign'
      this.getCampaign()
    }
    if (this.user) this.getPageContent()
    // this.setAutoSaveInterval()
  },
  beforeDestroy () {
    // this.clearAutoSaveInterval()
  },
  watch: {
    user (newUser) {
      this.getPageContent()
    },
    /**
     * Watch for changes to countries
     */
    countries (newValue, oldValue) {
      // If is edit page, set selected countries
      if (this.isEditPage) this.setSelectedCountries()
    },
    /**
     * Watch for changes to selected countries
     */
    selectedCountries (newValue, oldValue) {
      this.getStates()
      this.getCities()
    }
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    /**
     *  check if daily budget is bigger than minimum daily budget
     */
    checkIfDailyBudgetIsBiggerThanMinimumDailyBudget () {
      if (this.suggestedEndDate && this.form.amount > 0) {
        const startDate = moment(this.form.start_date)
        const endDate = moment(this.form.end_date)
        const campaignDays = endDate.diff(startDate, 'days') + 1
        const totalBudget = campaignDays * this.minimumDailyBudget

        return this.form.amount >= totalBudget
      }
      return true
    },
    /**
     * Get cities options based on the selected states of the line
     */
    citiesOptions () {
      /* const states = this.form.states.map(state => {
        return state.label
      }) */
      const cities = this.cities.reduce((filtered, item) => {
        /* if (states.includes(item.state_name)) {
          filtered.push({
            label: `${item.name} (${item.state_short_name})`,
            code: item.id
          })
        } */
        filtered.push({
          label: `${item.name} (${item.state_short_name} - ${item.country_short_name})`,
          code: item.id
        })
        return filtered
      }, [])
      return cities
    },
    /**
     * Get countries options
     */
    countryOptions () {
      return this.countries.map(item => {
        return {
          label: `${item.name} (${item.short_name})`,
          code: item.id
        }
      })
    },
    /**
     * Check if campaign amount has exceeded what advertiser has available
     */
    hasCampaignExceededAvailableAdvertiserAmount () {
      if (!this.selectedAdvertiser) return true
      // If is new campaign, the only thing that matters is the full available amount
      const amount = this.form.amount * 100
      const available = this.selectedAdvertiser.available_amount
      if (!this.campaign) {
        return amount > available
      }

      // If is campaign that already exists, calculate the difference between new amount and old amount and compare that value to available amount
      const difference = amount - this.campaign.amount
      return difference > available
    },
    /**
     * Is campaign end date valid
     */
    isCampaignEndDateValid () {
      if (!this.form.end_date) return false
      const date = moment(this.form.end_date)
      // Check if is update and if value hasnt changed from original
      if (this.campaign && !this.isCampaignDraft) {
        const then = moment(this.campaign.end_date)
        if (date.isSame(then)) return true
      }
      const tomorrow = moment().startOf('day').add(1, 'day')
      return date.isSameOrAfter(tomorrow)
    },
    /**
     * Is campaign status draft
     */
    isCampaignDraft () {
      return this.campaign.status === 'DRAFT'
    },
    /**
     * Checks if campaign end date is before start date
     */
    isCampaignEndDateBeforeStartDate () {
      if (!this.form.end_date || !this.form.start_date) return true
      const startDate = moment(this.form.start_date)
      const endDate = moment(this.form.end_date)

      return endDate.isSameOrBefore(startDate)
    },
    /**
     * Check if campaign period is below recommended
     */
    isCampaignPeriodBelowRecommended () {
      if (!this.form.start_date || !this.form.end_date) return true
      const start = moment(this.form.start_date)
      const end = moment(this.form.end_date)
      return end.diff(start, 'days') < 10
    },
    /**
     * Is campaign start date valid
     */
    isCampaignStartDateValid () {
      if (!this.form.start_date) return false
      const date = moment(this.form.start_date)
      if (this.campaign && !this.isCampaignDraft) {
        const then = moment(this.campaign.start_date)
        if (date.isSame(then)) return true
      }
      const minimumDate = moment().endOf('day').add(3, 'days')
      return date.isSameOrAfter(minimumDate)
    },
    /**
     * Check if campaign is complete
     */
    isCampaignStepComplete () {
      if (
        !this.form.advertiser_id ||
        !this.form.name ||
        !this.form.start_date ||
        !this.form.end_date ||
        !this.form.audience_description ||
        parseFloat(this.form.amount) < 5000 ||
        this.hasCampaignExceededAvailableAdvertiserAmount ||
        !this.isCampaignStartDateValid ||
        !this.isCampaignEndDateValid ||
        this.isCampaignEndDateBeforeStartDate ||
        !this.selectedAdvertiser ||
        this.selectedAdvertiser.status === 'ERROR'
      ) {
        return false
      }

      return true
    },
    /**
     * Check if creative step is complete
     */
    isCreativeStepComplete () {
      return (
        this.form.creatives.length > 0
      )
    },
    /**
     * Check if is edit page
     */
    isEditPage () {
      return this.$route.name === 'campaigns.edit'
    },
    /**
     * Is form complete
     */
    isFormComplete () {
      return (
        this.isCampaignStepComplete &&
        this.isCreativeStepComplete
      )
    },
    /**
     * Get previous step
     */
    previousStep () {
      switch (this.step) {
        case 'campaign':
          return null
        default:
          return null
      }
    },
    /**
     * Router name
     */
    routeName () {
      return this.$route.name
    },
    /**
     * Selected advertiser
     */
    selectedAdvertiser () {
      if (!this.form.advertiser_id) return null

      return this.advertisers.find(item => {
        this.money = getMoneyFormat(item.currency)

        return item.id === this.form.advertiser_id
      })
    },
    /**
     * show alert on cities and states selection
     */
    showAlertOnCitiesAndStatesSelection () {
      const selectedStates = this.form.states.map((state) => {
        const stateData = this.states.find((s) => s.id === state.code)
        return stateData?.name || ''
      })

      const selectedCities = this.form.cities.map((city) => {
        const cityData = this.cities.find((c) => c.id === city.code)
        return cityData ? { name: cityData.name, state_name: cityData.state_name } : null
      }).filter(city => city !== null)

      if (selectedStates.length && selectedCities.length) {
        // Check if the cities are within the selected state
        const citiesOutOfState = selectedCities.filter((city) =>
          !selectedStates.includes(city.state_name)
        )

        if (citiesOutOfState.length) {
          return this.$t('region_selection_states_and_cities', {
            states: selectedStates.join(', '),
            cities: [...new Set(citiesOutOfState.map((city) => city.state_name))].join(', ')
          })
        } else {
          return this.$t('region_selection_city_inside_state')
        }
      }

      if (selectedStates.length > 0) {
        return this.$t('region_selection_only_states')
      }

      if (selectedCities.length > 0) {
        return this.$t('region_selection_only_cities')
      }
      return false
    },
    /**
     * State options for select 2 field
     */
    stateOptions () {
      return this.states.map(state => {
        return {
          label: `${state.name} (${state.country_short_name})`,
          code: state.id
        }
      })
    },
    /**
     * Step description
     */
    stepDescription () {
      switch (this.step) {
        case 'campaign':
          return this.$t('create_basic_campaign_settings')
        default:
          return ''
      }
    },
    /**
     * calculate campaign end date, minimum value 10 reais per day
     */
    suggestedEndDate () {
      if (!this.form.start_date || !this.form.end_date) return null

      const startDate = moment(this.form.start_date)

      const suggestedEndDate = startDate.clone().add(Math.floor(this.form.amount / this.minimumDailyBudget), 'days').subtract(1, 'day')
      return suggestedEndDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    }
  },
  methods: {
    /**
     * Add all selected advertiser available amount to campaign
     */
    addAllAvailableAmountToCampaign () {
      if (!this.selectedAdvertiser) return
      // If is creation, use full amount
      if (!this.isEditPage) {
        this.form.amount = (this.selectedAdvertiser.available_amount / 100)
      } else {
        // If is edit page, add the amount to what is in the form
        this.form.amount = this.form.amount + (this.selectedAdvertiser.available_amount / 100)
      }
    },
    /**
     * Clear form property based on a given parameter
     */
    clearSelection (selection) {
      this.form[selection] = []
    },
    /**
     * Change step of form
     */
    changeStep (step) {
      this.step = step
    },
    /**
     * change accordion item
     */
    changeAccordionItem (item) {
      this.$root.$emit('bv::toggle::collapse', item)
    },
    /**
     * Clear auto save interval
     */
    clearAutoSaveInterval () {
      // this.isAutoSaveEnabled = false
      clearInterval(this.autoSaveInterval)
    },
    /**
     * On click submit button
     */
    clickSubmit () {
      this.submit(false)
    },
    /**
     * Get advertisers
     */
    async getAdvertisers () {
      this.advertisersLoader = true
      try {
        const advertisers = await AdvertisersService.getAdvertisers({
          companyId: this.user.company.id,
          itemsPerPage: 100,
          status: 'ACTIVE',
          viewType: 'listing_for_campaign_creation'
        })
        this.advertisers = advertisers.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.advertisersLoader = false
    },
    /**
     * Get advertiser
     */
    async getCampaign () {
      if (!this.baseCampaign) return
      this.campaign = this.baseCampaign
      this.setCampaign(this.baseCampaign)
    },
    /**
     * Get cities
     */
    async getCities () {
      if (this.selectedCountries.length === 0) {
        this.cities = []
        return
      }
      this.citiesLoader = true
      try {
        const cities = await HelpersService.getCities({
          countryId: this.selectedCountries.map(item => {
            return item.code
          })
        })
        this.cities = cities
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.citiesLoader = false
    },
    /**
     * Get countries
     */
    async getCountries () {
      this.countriesLoader = true
      try {
        const countries = await HelpersService.getCountries()
        this.countries = countries
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.countriesLoader = false
    },

    /**
     * Get necessary page content
     */
    getPageContent () {
      this.getAdvertisers()
      this.getCountries()
      // this.getStates()
      // this.getCities()
    },
    /**
     * Get states
     */
    async getStates () {
      if (this.selectedCountries.length === 0) {
        this.states = []
        return
      }
      this.statesLoader = true
      try {
        const states = await HelpersService.getStates({
          country_id: this.selectedCountries.map(item => {
            return item.code
          })
        })
        this.states = states
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.statesLoader = false
    },
    /**
     * Handle selected creative
     */
    handleSelectedCreative (creatives) {
      // Check if creative is not already selected
      creatives.forEach(creative => {
        const index = this.form.creatives.findIndex(item => {
          return item.id === creative.id
        })
        if (index < 0) {
          this.form.creatives.push(creative)
          this.creatives.push(creative)
        }
      })
    },
    /**
     * Destroy creative
     */
    destroyCreative (creative) {
      const index = this.form.creatives.findIndex(item => {
        return item.id === creative.id
      })
      if (index < 0) return
      this.form.creatives.splice(index, 1)
    },
    /**
     * Open select creative modal
     */
    openSelectCreativeModal (type) {
      this.currentCreativeSelection = type
      this.$bvModal.show('select-creative-modal')
    },
    /**
     * Open destroy creative modal
     */
    openDestroyCreativeModal (creative) {
      this.selectedCreative = creative
      this.$bvModal.show('destroy-creative-modal')
    },
    /**
     * Save draft
     */
    saveDraft () {
      if (!this.selectedAdvertiser) return
      if (this.campaign && this.campaign.status !== 'DRAFT') return
      this.submit(true)
    },
    /**
     * Select all states
     */
    selectAllStates () {
      const all = []
      this.stateOptions.forEach(item => {
        all.push({
          label: item.label,
          code: item.code
        })
      })
      this.form.states = all
    },
    /**
     * Create auto save interval
     */
    setAutoSaveInterval () {
      // this.isAutoSaveEnabled = true
      this.autoSaveInterval = setInterval(() => {
        this.saveDraft()
      }, 30000)
    },
    /**
     * Set campaign
     */
    setCampaign (campaign) {
      this.form.advertiser_id = campaign.advertiser.id
      this.form.name = campaign.name
      this.form.start_date = campaign.start_date
      this.form.end_date = campaign.end_date
      this.form.audience_description = campaign.audience_description
      this.form.amount = campaign.amount / 100
      this.form.states = campaign.states.map(s => {
        return {
          label: s.name,
          code: s.id
        }
      })
      this.form.cities = campaign.cities.map(c => {
        return {
          label: `${c.name} (${c.state_short_name})`,
          code: c.id
        }
      })
      this.setSelectedCountries()
      campaign.creatives.forEach(creative => {
        this.form.creatives.push(creative)
      })
    },
    /**
     * Set selected countries
     */
    setSelectedCountries () {
      if (!this.campaign) return
      const countries = []
      this.campaign.states.forEach(item => {
        if (!countries.includes(item.country_short_name)) countries.push(item.country_short_name)
      })
      this.campaign.cities.forEach(item => {
        if (!countries.includes(item.country_short_name)) countries.push(item.country_short_name)
      })
      this.selectedCountries = this.countries.filter(item => {
        return countries.includes(item.short_name)
      }).map(item => {
        return {
          label: `${item.name} (${item.short_name})`,
          code: item.id
        }
      })
    },
    /**
     * Submit
     */
    async submit (isDraft = false) {
      // this.clearAutoSaveInterval()
      if (isDraft) this.isSavingDraft = true
      else this.loader = true
      try {
        // Create copy of form that can be changed (object assign wont work for multi-layered objects)
        const form = JSON.parse(JSON.stringify(this.form))
        // Parse amounts
        form.amount = parseInt(form.amount * 100)
        form.cities = this.form.cities.map(item => {
          return item.code
        })
        form.states = this.form.states.map(state => {
          return state.code
        })
        if (form.creatives.length > 0) {
          form.creative_ids = form.creatives.map(item => {
            return item.id
          })
        }
        let campaign = null
        form.is_draft = isDraft
        if (!this.campaign) {
          campaign = await CampaignsService.createCampaign(form)
        } else {
          campaign = await CampaignsService.updateCampaign(this.campaign.id, form)
        }
        if (!isDraft) {
          this.$router.push({ name: 'campaigns.show', params: { id: campaign.id } })
          const notification = {
            notification: {
              type: 'success',
              content: this.$t('campaign_in_creation_process')
            }
          }
          NotificationService.createGlobalSuccessNotification(notification)
        } else {
          this.campaign = campaign
        }
      } catch (error) {
        // console.log(error)
        error.handleGlobally && error.handleGlobally()
      }
      if (isDraft) this.isSavingDraft = false
      else this.loader = false
      // Reenable autosave
      // if (isDraft) this.setAutoSaveInterval()
    }
  }
}
</script>

<style lang="css" src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
  ::v-deep {
    .native-campaign-navigation {
      position: absolute;
      top: 48px;
      left: -100px;
      z-index: 2;
      display: none;

      @include desktop {
        display: block;
      }
    }
  }
</style>
